/**
 * About Page Component
 * Comprehensive product and company description for Google Startup Program verification
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './AboutPage.css';

function AboutPage() {
  const headerRef = useRef(null);

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.pageYOffset > 50) {
          headerRef.current.classList.add('scrolled');
        } else {
          headerRef.current.classList.remove('scrolled');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.about-page .reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Desktop screenshots (1081x958, landscape)
  const desktopScreenshots = [
    {
      src: '/screenshots/desktop-video-analysis.jpg',
      alt: 'LumiSkating desktop video analysis with pose overlay',
      caption: 'AI-powered pose detection and biomechanical analysis',
    },
    {
      src: '/screenshots/mobile-video-upload.jpg',
      alt: 'LumiSkating video upload and analysis history',
      caption: 'Upload videos and track analysis history',
    },
  ];

  // Mobile screenshots (portrait)
  const mobileScreenshots = [
    {
      src: '/screenshots/mobile-coaching-chat.jpg',
      alt: 'LumiSkating AI coaching chat on mobile',
      caption: 'Personalized AI coaching 24/7',
    },
    {
      src: '/screenshots/mobile-landing.jpg',
      alt: 'LumiSkating responsive landing page on mobile',
      caption: 'Responsive design for mobile',
    },
    {
      src: '/screenshots/Note.jpg',
      alt: 'LumiSkating coach notes and training journal',
      caption: 'Coach notes and training journal',
    },
    {
      src: '/screenshots/Theme.jpg',
      alt: 'LumiSkating customizable themes',
      caption: 'Customizable themes and settings',
    },
  ];

  return (
    <div className="about-page">
      {/* SEO Meta Tags */}
      <SEO
        title="About LumiSkating - AI-Powered Figure Skating Training Technology"
        description="LumiSkating is an Olympic-level AI training assistant for figure skaters. Learn about our technology, features, and mission to democratize elite skating coaching."
        url="https://www.lumiskating.com/about"
      />

      {/* Navigation */}
      <header className="about-header" ref={headerRef}>
        <nav className="about-nav">
          <Link to="/" className="nav-brand">
            <div className="brand-mark">
              <img src="/skate.png" alt="LumiSkating Logo" width="38" height="38" />
            </div>
            <div className="brand-text">
              <span className="brand-name">Lumi<span className="brand-skating">Skating</span></span>
            </div>
          </Link>
          <div className="nav-links">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/about" className="nav-link active">About</Link>
            <Link to="/" className="btn-nav-cta">Get Started</Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span>AI-Powered Training Technology</span>
          </div>
          <div className="dev-stage-badge">
            <span className="pulse-dot"></span>
            <span className="stage-label">Live Beta</span>
          </div>
          <h1>About LumiSkating</h1>
          <p className="hero-subtitle">
            Bringing Olympic-caliber coaching intelligence to every figure skater through
            advanced AI video analysis and personalized feedback.
          </p>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="about-section">
        <div className="section-container">
          <div className="section-content reveal-on-scroll">
            <h2>What We Do</h2>
            <p className="lead-text">
              LumiSkating is an AI-powered figure skating training assistant designed to provide
              Olympic-level coaching insights to skaters of all competitive levels.
            </p>
            <p>
              Our platform combines cutting-edge computer vision technology with deep knowledge of
              figure skating biomechanics to deliver real-time video analysis, personalized coaching
              feedback, and comprehensive training planning tools.
            </p>
            <p>
              By leveraging advanced AI, we transform ordinary training videos into detailed
              biomechanical assessments, helping skaters understand exactly what they need to
              improve and how to achieve their goals.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="about-section mission-section">
        <div className="section-container">
          <div className="mission-content reveal-on-scroll">
            <div className="mission-statement">
              <h2>Our Mission</h2>
              <blockquote>
                "To democratize Olympic-level figure skating coaching by making
                advanced biomechanical analysis and AI-powered feedback accessible
                to every skater, everywhere."
              </blockquote>
            </div>
            <div className="mission-values">
              <div className="value-item">
                <h4>Accessibility</h4>
                <p>Elite-level analysis tools available to skaters at every level</p>
              </div>
              <div className="value-item">
                <h4>Scientific Rigor</h4>
                <p>Biomechanically accurate analysis grounded in sports science</p>
              </div>
              <div className="value-item">
                <h4>Coach Partnership</h4>
                <p>Enhancing—never replacing—the irreplaceable human coaching relationship</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="about-section tech-section">
        <div className="section-container">
          <div className="section-header reveal-on-scroll">
            <h2>Our Technology</h2>
            <p>Built on industry-leading AI and computer vision frameworks</p>
          </div>
          <div className="tech-grid">
            <div className="tech-card reveal-on-scroll">
              <div className="tech-icon">
                <i className="fas fa-brain"></i>
              </div>
              <h3>Google Gemini 3.0 Pro</h3>
              <p>
                Our intelligent coaching engine is powered by Google's most advanced AI model,
                providing context-aware feedback that understands figure skating at an expert level.
              </p>
            </div>
            <div className="tech-card reveal-on-scroll">
              <div className="tech-icon">
                <i className="fas fa-eye"></i>
              </div>
              <h3>MediaPipe Pose Detection</h3>
              <p>
                33-point body tracking captures every nuance of your movement with sub-centimeter
                precision, analyzing body position, joint angles, and movement patterns in real-time.
              </p>
            </div>
            <div className="tech-card reveal-on-scroll">
              <div className="tech-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3>Biomechanics Analysis</h3>
              <p>
                Frame-by-frame analysis measures jump height, rotation speed, center of mass trajectory,
                air position, and landing quality with scientific accuracy.
              </p>
            </div>
            <div className="tech-card reveal-on-scroll">
              <div className="tech-icon">
                <i className="fas fa-medal"></i>
              </div>
              <h3>ISU Standards Alignment</h3>
              <p>
                Scoring feedback aligned with International Skating Union technical standards,
                helping skaters understand how their elements would be evaluated in competition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase & Demo Video Section */}
      <section className="about-section showcase-section">
        <div className="section-container">
          <div className="section-header reveal-on-scroll">
            <h2>See LumiSkating in Action</h2>
            <p>Our platform is in live beta — here's what it looks like</p>
          </div>

          {/* Demo Video */}
          <div className="demo-video-container reveal-on-scroll">
            <div className="demo-video-crop">
              <iframe
                src="https://www.youtube-nocookie.com/embed/4kd3_Lbsk8M?modestbranding=1&rel=0&iv_load_policy=3&showinfo=0"
                title="LumiSkating Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          {/* Screenshot Gallery - Desktop */}
          <div className="screenshot-gallery-desktop reveal-on-scroll">
            {desktopScreenshots.map((shot, index) => (
              <div className="screenshot-item" key={index}>
                <div className="screenshot-img-wrapper">
                  <img src={shot.src} alt={shot.alt} loading="lazy" />
                </div>
                <p className="screenshot-caption">{shot.caption}</p>
              </div>
            ))}
          </div>

          {/* Screenshot Gallery - Mobile */}
          <div className="screenshot-gallery-mobile reveal-on-scroll">
            {mobileScreenshots.map((shot, index) => (
              <div className="screenshot-item screenshot-item-mobile" key={index}>
                <div className="screenshot-img-wrapper">
                  <img src={shot.src} alt={shot.alt} loading="lazy" />
                </div>
                <p className="screenshot-caption">{shot.caption}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="showcase-cta reveal-on-scroll">
            <Link to="/" className="btn-primary">
              <span>Try LumiSkating Now</span>
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Enhance Training Section */}
      <section className="about-section problem-section">
        <div className="section-container">
          <div className="problem-content">
            <div className="problem-text reveal-on-scroll">
              <h2>How We Enhance Your Training</h2>
              <p className="lead-text">
                LumiSkating works alongside your coach to provide additional support and insights,
                helping you maximize every training session.
              </p>
              <ul className="problem-list">
                <li>
                  <i className="fas fa-circle"></i>
                  <span>24/7 AI assistance available whenever you need feedback between coaching sessions</span>
                </li>
                <li>
                  <i className="fas fa-circle"></i>
                  <span>Objective video analysis tools to complement your coach's expert eye</span>
                </li>
                <li>
                  <i className="fas fa-circle"></i>
                  <span>Detailed biomechanics data to share with your coach for deeper discussions</span>
                </li>
                <li>
                  <i className="fas fa-circle"></i>
                  <span>Track progress over time with metrics that support your coaching goals</span>
                </li>
              </ul>
              <p className="solution-text">
                <strong>LumiSkating enhances your existing coaching relationship</strong>, providing
                tools and insights that help you and your coach work together more effectively.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="about-section audience-section">
        <div className="section-container">
          <div className="section-header reveal-on-scroll">
            <h2>Who LumiSkating Is For</h2>
            <p>Designed for serious figure skaters and the coaches who train them</p>
          </div>
          <div className="audience-grid">
            <div className="audience-card reveal-on-scroll">
              <div className="audience-icon">
                <i className="fas fa-skating"></i>
              </div>
              <h3>Competitive Skaters</h3>
              <p>
                From Pre-Juvenile through Senior level skaters preparing for regional,
                sectional, national, and international competitions.
              </p>
            </div>
            <div className="audience-card reveal-on-scroll">
              <div className="audience-icon">
                <i className="fas fa-chalkboard-teacher"></i>
              </div>
              <h3>Figure Skating Coaches</h3>
              <p>
                Coaches seeking advanced analysis tools to provide more detailed,
                objective feedback to their students.
              </p>
            </div>
            <div className="audience-card reveal-on-scroll">
              <div className="audience-icon">
                <i className="fas fa-users"></i>
              </div>
              <h3>Training Programs</h3>
              <p>
                Skating clubs and training centers looking to integrate technology
                into their coaching methodology.
              </p>
            </div>
            <div className="audience-card reveal-on-scroll">
              <div className="audience-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <h3>Competition Preparation</h3>
              <p>
                Skaters and coaches preparing for ISU qualifying competitions who need
                to optimize every element for maximum scoring potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Development Stage & Business Model */}
      <section className="about-section business-section">
        <div className="section-container">
          <div className="section-header reveal-on-scroll">
            <h2>Development Stage & Business Model</h2>
            <p>A scalable SaaS platform built on Google Cloud</p>
          </div>
          <div className="business-content reveal-on-scroll">
            <div className="business-stage">
              <div className="stage-indicator">
                <span className="pulse-dot"></span>
                <span className="stage-text">Private Beta</span>
              </div>
              <p>
                LumiSkating is a digital-native SaaS platform currently in the Private Beta phase,
                actively onboarding competitive skaters and refining our AI analysis pipeline.
              </p>
            </div>
            <div className="business-grid">
              <div className="business-card reveal-on-scroll">
                <div className="business-icon">
                  <i className="fas fa-user-tag"></i>
                </div>
                <h3>B2C Subscription Model</h3>
                <p>
                  Tiered subscription plans for individual competitive skaters and families,
                  providing unlimited AI video analysis and personalized training logs.
                </p>
              </div>
              <div className="business-card reveal-on-scroll">
                <div className="business-icon">
                  <i className="fas fa-handshake"></i>
                </div>
                <h3>B2B Partnerships</h3>
                <p>
                  Enterprise-level licensing for skating clubs, training centers, and national
                  federations to integrate biomechanical analysis into their coaching workflows.
                </p>
              </div>
              <div className="business-card reveal-on-scroll">
                <div className="business-icon">
                  <i className="fas fa-cloud"></i>
                </div>
                <h3>Scalable Infrastructure</h3>
                <p>
                  Built entirely on Google Cloud, designed to scale globally and deliver
                  Olympic-level insights to the skating community regardless of location.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="about-section features-section">
        <div className="section-container">
          <div className="section-header reveal-on-scroll">
            <h2>Platform Features</h2>
            <p>Comprehensive tools for every aspect of skating training</p>
          </div>
          <div className="features-grid">
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-video"></i></div>
              <div className="feature-content">
                <h4>Video Upload & Analysis</h4>
                <p>Upload training videos from any device and receive instant AI-powered analysis</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-user-astronaut"></i></div>
              <div className="feature-content">
                <h4>Real-Time Pose Overlay</h4>
                <p>Visualize body position and joint angles directly on your video</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-ruler-vertical"></i></div>
              <div className="feature-content">
                <h4>Jump Analysis</h4>
                <p>Measure jump height, rotation count, air position, and landing quality</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-sync-alt"></i></div>
              <div className="feature-content">
                <h4>Spin Analysis</h4>
                <p>Track rotation speed, centering, and position changes throughout spins</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-comments"></i></div>
              <div className="feature-content">
                <h4>AI Coaching Feedback</h4>
                <p>Personalized coaching insights powered by Google Gemini AI</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-chart-area"></i></div>
              <div className="feature-content">
                <h4>Progress Tracking</h4>
                <p>Compare performances over time and track improvement metrics</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-calendar-alt"></i></div>
              <div className="feature-content">
                <h4>Training Planning</h4>
                <p>AI-assisted schedule generation and goal setting tools</p>
              </div>
            </div>
            <div className="feature-item reveal-on-scroll">
              <div className="feature-icon"><i className="fas fa-sticky-note"></i></div>
              <div className="feature-content">
                <h4>Coach Notes</h4>
                <p>Document observations, track patterns, and maintain training records</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="about-section contact-section">
        <div className="section-container">
          <div className="contact-content reveal-on-scroll">
            <h2>Contact Us</h2>
            <p>
              Have questions about LumiSkating? We'd love to hear from you.
            </p>
            <div className="contact-methods">
              <a href="mailto:support@lumiskating.com" className="contact-method">
                <i className="fas fa-envelope"></i>
                <span>support@lumiskating.com</span>
              </a>
            </div>
            <div className="cta-buttons">
              <Link to="/" className="btn-primary">
                <span>Try LumiSkating</span>
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
            <p className="founder-note">
              Founded by an AI engineer and skating parent who understands what
              competitive skating families need—because we are one.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="about-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <div className="brand-mark">
              <img src="/skate.png" alt="LumiSkating Logo" width="32" height="32" />
            </div>
            <span className="brand-name">Lumi<span className="brand-skating">Skating</span></span>
          </div>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/about">About</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href="mailto:support@lumiskating.com">Contact</a>
          </div>
          <p className="footer-copyright">
            &copy; 2025-2026 LumiSkating. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default AboutPage;
